import { useEffect, useState } from 'react';
import { ADMIN_UPLOAD_MAX_BYTES, type AdminUploadKind } from '@qaytim/shared';

import { Field } from '@/components/field';
import { Input } from '@/components/ui/input';
import { useUploadAdminImage } from '@/lib/admin-queries';
import { describeError } from '@/lib/error-text';
import { strings } from '@/i18n/strings';

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export interface ImageUploadFieldProps {
  /** Saqlash joyidagi kalit prefiksini hal qiladi — banner yoki ball-katalog rasmi. */
  kind: AdminUploadKind;
  label: string;
  hint?: string;
  /** Mavjud bandning saqlangan rasmi — hali fayl tanlanmagan bo'lsa shu ko'rsatiladi. */
  imageUrl?: string | null;
  onUploaded: (key: string) => void;
  disabled?: boolean;
  /** Har bir maydonga alohida `aria-label` kerak bo'lganda (masalan qatordagi tahrirlash formasi). */
  inputAriaLabel?: string;
}

/**
 * Rasm tanlash + oldindan ko'rish, banner va ball-katalog formalarida bir xil.
 *
 * Fayl tanlangan zahoti yuklash boshlanadi — submitgacha kutilmaydi. Sabab: operator "Qo'shish"
 * tugmasini bosgunicha rasm allaqachon saqlash joyida turishi kerak, aks holda forma ikkita
 * tarmoq so'rovini ketma-ket kutib turadigan bo'lardi va oraliqdagi xatoni ajratib bo'lmasdi.
 */
export function ImageUploadField({
  kind,
  label,
  hint,
  imageUrl,
  onUploaded,
  disabled,
  inputAriaLabel,
}: ImageUploadFieldProps) {
  const upload = useUploadAdminImage();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  // Ob'ekt-URL komponent yo'q qilinganda yoki yangi fayl tanlanganda bo'shatiladi — aks holda
  // brauzer xotirasida ishlatilmagan rasm qoldiqlari to'planib qoladi.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.has(file.type)) {
      setLocalError(strings.uploads.typeInvalid);
      event.target.value = '';
      return;
    }
    if (file.size > ADMIN_UPLOAD_MAX_BYTES) {
      setLocalError(strings.uploads.tooLarge);
      event.target.value = '';
      return;
    }
    setLocalError(null);

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return objectUrl;
    });

    upload.mutate({ file, kind }, { onSuccess: onUploaded });
  };

  const preview = previewUrl ?? imageUrl ?? null;

  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-3">
        {preview ? (
          <img
            src={preview}
            alt={strings.uploads.thumbnailAlt}
            className="border-input size-14 shrink-0 rounded-md border object-cover"
          />
        ) : (
          <div className="border-input bg-muted text-muted-foreground flex size-14 shrink-0 items-center justify-center rounded-md border text-center text-[10px] leading-tight">
            {strings.uploads.noImage}
          </div>
        )}
        <Input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          aria-label={inputAriaLabel ?? label}
          disabled={disabled || upload.isPending}
          onChange={handleChange}
        />
      </div>
      {upload.isPending ? (
        <p className="text-muted-foreground text-xs">{strings.uploads.uploading}</p>
      ) : null}
      {localError ? <p className="text-destructive text-xs">{localError}</p> : null}
      {upload.isError ? (
        <p className="text-destructive text-xs">{describeError(upload.error)}</p>
      ) : null}
    </Field>
  );
}
